import React, { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { Sidebar } from '@/components/Sidebar'
import { FloatingUploadIndicator } from '@/features/file-upload'
import { useGlobalSearchStore } from '@/features/global-search/store'
import { NotificationDrawer } from '@/features/notification'
import { useNotificationDrawerStore } from '@/features/notification/store'
import { NO_PADDING_ROUTES } from '@/utils/routes'

import { cn } from '../../utils'

/**
 * MainLayout Component
 *
 * A desktop layout with a left panel and main content area.
 *
 * The left panel includes: top logo area, scrollable menu section, and bottom user info.
 */
export const MainLayout: React.FC = () => {
  const { setDialogOpen } = useGlobalSearchStore()
  const {
    isOpen: isNotificationDrawerOpen,
    isPinned: isNotificationDrawerPinned,
  } = useNotificationDrawerStore()
  const location = useLocation()

  const shouldApplyPadding = !(NO_PADDING_ROUTES as readonly string[]).includes(
    location.pathname
  )

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if the pressed key is '/' and not in an input field
      if (
        event.key === '/' &&
        !['INPUT', 'TEXTAREA'].includes((event.target as HTMLElement).tagName)
      ) {
        event.preventDefault()
        setDialogOpen(true)
      }
    }

    globalThis.addEventListener('keydown', handleKeyDown)

    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown)
    }
  }, [setDialogOpen])

  return (
    <div className="flex h-screen overflow-hidden bg-primary">
      {/* Left Panel */}
      <Sidebar />

      {/* Main Content Area */}
      <main
        className={cn(
          'flex-1 h-full overflow-y-auto transition-all duration-300 ease-in-out',
          shouldApplyPadding && 'p-4xl',
          isNotificationDrawerOpen &&
            isNotificationDrawerPinned &&
            'ml-notification-drawer'
        )}
      >
        <Outlet />
      </main>

      {/* Floating Upload Indicator - shown during uploads when not on upload page */}
      <FloatingUploadIndicator />

      {/* Notification Drawer - shown when notification icon is clicked in sidebar */}
      <NotificationDrawer />
    </div>
  )
}

export default MainLayout
